import { t } from 'i18next';

import { generalConfig } from '@sola/config/lib/getters.ts';
import { MergeField, MergeFieldOptions } from '@sola/schema/lib/contentful.ts';

/**
 * Merge fields optional variant suffix in placeholders, e.g.:
 *   {{UNAVAILABLE_CHILD_NAME}}          -> default
 *   {{UNAVAILABLE_CHILD_NAME, c}}       -> cap (if available), else default
 */
type Variant = 'default' | 'cap';

/**
 * Resolve a value (string or { default, cap }) to a string using the desired variant.
 * If the requested variant doesn't exist, it falls back to 'default' if present.
 */
const resolveVariant = (val: unknown, variant: Variant): string | undefined => {
  if (val && typeof val === 'string') {
    return val;
  }

  if (val && typeof val === 'object' && !Array.isArray(val)) {
    const obj = val as Record<string, unknown>;
    const candidate = obj[variant];
    if (candidate && typeof candidate === 'string') {
      return candidate as string;
    }

    const { default: defaultVal } = obj;
    if (defaultVal && typeof defaultVal === 'string') {
      return defaultVal as string;
    }
  }

  return undefined;
};

/**
 * Pick a value from primary -> fallback -> base fallback, honoring the requested variant.
 */
const pickWithFallbacks = (
  key: MergeField,
  variant: Variant,
  primary: MergeFieldOptions | undefined,
  fallback: MergeFieldOptions | undefined,
): string => {
  const fromPrimary = primary
    ? resolveVariant(primary[key], variant)
    : undefined;
  if (fromPrimary !== undefined) {
    return fromPrimary;
  }

  const fromFallback = fallback
    ? resolveVariant(fallback[key], variant)
    : undefined;
  if (fromFallback !== undefined) {
    return fromFallback;
  }

  return '';
};

/**
 * Merge fields with optional variant suffix in placeholders, e.g.:
 *   {{UNAVAILABLE_CHILD_NAME}}          -> default
 *   {{UNAVAILABLE_CHILD_NAME, c}}       -> cap (if available), else default
 *
 * Notes:
 * - If the primary merge value is a plain string (not an object), it is used as-is,
 *   even when ', c' is requested. The variant logic applies during fallback.
 * - Unknown keys resolve to '' (empty string).
 */
export const mergeFields = (
  input: string,
  mergeValues: MergeFieldOptions,
  fallbackValues?: MergeFieldOptions,
) => {
  if (!input || !mergeValues) {
    return input;
  }

  const rate = mergeValues.CDSP_RATE || generalConfig.rate;
  const allMergeValues: MergeFieldOptions = {
    ...mergeValues,
    CDSP_RATE: t('priority:mergeFallbacks.rate', {
      rate,
      defaultValue: `$${rate}`,
    }),
  };

  /** Matches: {{ KEY }} or {{ KEY, opt }}
   * Ex: {{UNAVAILABLE_CHILD_NAME, c}}
   */
  const placeholderRegex = /{{\s*([A-Z0-9_]+)\s*(?:,\s*([a-zA-Z]+)\s*)?}}/g;

  const replaced = input.replace(
    placeholderRegex,
    (_match, rawKey: string, opt?: string) => {
      const key = rawKey as MergeField;

      // Right now we only care about 'c' -> 'cap'; default is 'default'
      const variant: Variant = opt?.toLowerCase() === 'c' ? 'cap' : 'default';

      // Special case: if primary has a string, use it as-is regardless of 'c'
      const primaryVal = allMergeValues[key];

      if (primaryVal && typeof primaryVal === 'string') {
        return primaryVal;
      }

      // Otherwise, use the variant-aware fallback chain
      return pickWithFallbacks(key, variant, allMergeValues, fallbackValues);
    },
  );

  return replaced;
};
